<template>
    <tr
        :data-id="pharmacist?._id || pharmacistId"
        :class="pharmacist && assignedPharmacist?._id === pharmacist._id ? 'already-allocated' : ''"
    >
        <td v-if="hydrationError" colspan="8" class="hydration-error">
            <span>Freiberufler {{ pharmacistId }} konnte nicht geladen werden.</span>
            <v-btn class="retry-hydration ml-2" x-small @click="hydrateApplicant">
                Erneut versuchen
            </v-btn>
        </td>
        <template v-else-if="pharmacist">
        <td>
            <div :class="getPharmacistAvailabilityStatus.classes">
                <v-icon v-if="assignedPharmacist?._id === pharmacist._id"
                    class="mb-1 mr-1" small>
                    mdi-check-circle
                </v-icon>
                <a :href="`/pharmacists/${pharmacist._id}`" target="_blank">
                    {{ `${pharmacist.firstname} ${pharmacist.lastname}` }}
                </a>
            </div>
        </td>
        <td>{{ requestDate }}</td>
        <td :style="{ color: travelCostColor }">
            {{ travelCostDistance }} km / {{ travelCostCosts }} €
        </td>
        <td>
            <span
                :style="{
                    color: +pharmacist.erp_expertise[pharmacy.erp] >= 2 || pharmacist.erp_counts[pharmacy.erp] >= 2 ? 'green' : 'red'
                }"
            >
                {{ pharmacist.erp_expertise[pharmacy.erp] }}/3 ({{ pharmacist.erp_counts[pharmacy.erp] }})
            </span>
            |
            <span
                class="hba-status"
                :style="{ color: appointment.hba && !pharmacist.has_hba ? 'red' : 'green' }">
                {{ pharmacist.has_hba ? 'HBA vorhanden' : 'Kein HBA' }}
            </span>
        </td>
        <td
            class="known-pharmacist-status"
            :style="{ color: isKnownPharmacist ? 'green' : 'red' }"
        >
            <span>{{ isKnownPharmacist ? 'Ja' : 'Nein' }}</span>
            <v-icon
                v-if="isFavoritePharmacist"
                class="ml-1 favorite"
                small
            >
                mdi-heart
            </v-icon>
        </td>
        <td class="message">{{ requestMessage }}</td>
        <td>
            <v-btn
                @click="accept"
                :disabled="!selectionEnabled || !canManageRequests() || disableAcceptance || !getPharmacistAvailabilityStatus.canAssign"
                x-small
                color="success"
                class="accept"
            >
                Auswählen
            </v-btn>
            <template>
                <v-btn
                    @click="openRejectionDialog"
                    :disabled="!selectionEnabled || !canManageRequests() || disableRejection" x-small color="error"
                    class="reject"
                    >Absage
                </v-btn>
                <rejection-dialog
                    v-if="selectionEnabled"
                    v-model="isRejectionDialogOpen"
                    @confirm-rejection="reject"
                />
            </template>
        </td>
        <td :style="{
            color: requestStatus.toLowerCase() === 'accepted' ? 'green' :
                requestStatus.toLowerCase() === 'rerequested' ? 'orange' :
                requestStatus.toLowerCase() === 'cancellationwithdrawn' ? 'blue' :
                'red'
            }"
            class="status">
            {{ getStatusName }}
        </td>
        </template>
    </tr>
</template>

<script>
import axios from "axios";
import moment from 'moment';
import { mapMutations } from 'vuex';
import RejectionDialog from "./RejectionDialog.vue";

const STATUS_MAP = {
    rejected: 'Abgesagt',
    accepted: 'Zugesagt',
    cancelled: 'Storniert',
    auto_rejected_assigned: 'Automatisch abgelehnt (andere FB Zuweisung)',
    auto_rejected_delete: 'Automatisch abgelehnt (Löschung)',
    cancellationwithdrawn: 'Absage zurückgezogen',
    rerequested: 'Erneut angefragt',
};

export default {
    components: {
        RejectionDialog
    },
    props: {
        request: Array,
        pharmacistId: String,
        knownPharmacists: Array,
        assignedPharmacist: Object,
        pharmacy: Object,
        appointment: Object,
        group: Array,
        idx: Number,
        numOfRequests: Number,
        selectionEnabled: {
            type: Boolean,
            default: true,
        },
        hydrationRetryToken: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            pharmacist: null,
            requestMessage: this.request?.[0] || '',
            requestStatus: this.request?.[2] || 'NotSet',
            knownPharmacist: [],
            travelcostsInfo: {},
            disableAcceptance: ['accepted', 'cancellationwithdrawn'].includes((this.request?.[2] || '').toLowerCase()),
            disableRejection: !['notset', 'rerequested', 'cancellationwithdrawn'].includes((this.request?.[2] || '').toLowerCase()),
            isRejectionDialogOpen: false,
            hydrationError: false,
            isHydrating: false,
        }
    },
    methods: {
        canManageRequests() {
            const appointments = this.group?.length ? this.group : [this.appointment];
            return !this.assignedPharmacist?._id && appointments.every(appointment =>
                !appointment.assigned_pharmacist_id &&
                ['open', 'inquired'].includes(appointment.wf_state || appointment.state || appointment.status)
            );
        },
        async getPharmacist() {
            const response = await axios.get(`/api/contractors/${this.pharmacistId}`);
            this.pharmacist = response.data;
        },
        async getAppointmentCosts() {
            try {
                const appointmentId = this.normalizeId(this.appointment?._id || this.appointment?.id);
                const response = await axios.get(`/api/appointments/travelcosts`, {
                    params: {
                        appointment_id: appointmentId,
                        pharmacist_id: this.pharmacistId
                    }
                })
                const routeCosts = Array.isArray(response.data) ? response.data : [];
                this.travelcostsInfo = routeCosts.find(
                    ({ appointment_id }) => this.normalizeId(appointment_id) === appointmentId
                ) || {};
            } catch {
                this.travelcostsInfo = {};
            }
        },

        normalizeId(value) {
            if (value && typeof value === 'object') {
                return String(value.$oid || value.oid || value.id || value._id || '');
            }
            return value == null ? '' : String(value);
        },

        formatDate(date, format='DD-MM-yyyy HH:mm') {
            if (!date) return ''
            return moment(date + 'Z').format(format)
        },
        formatDistance(distance) {
            const numeric = Number(distance);
            if (!Number.isFinite(numeric)) return '-';
            return Math.ceil(numeric);
        },

        isDateMatch(dates) {
            if (!Array.isArray(dates)) return false;
            const appointments = Array.isArray(this.group) && this.group.length
                ? this.group
                : [{ ...(this.appointment || {}) }];
            return dates.some(date => {
                return appointments.some(appointment => {
                    return moment(date).isSame(moment(appointment.date))
                })
            });
        },

        getStatusCss(stateColor) {
            const cssClass = { 'pharmacist-name': true };
            return {
                ...cssClass,
                [stateColor]: true
            }
        },

        async reject(rejectionComment) {
            if (!this.selectionEnabled || !this.pharmacist) {
                this.isRejectionDialogOpen = false;
                return;
            }
            this.$emit('is-loading', true);
            try {
                const response = await axios.post(`/api/contractor/${this.pharmacist._id}/reject_request/${this.appointment._id}`,{
                    'comment': rejectionComment
                });
                if (response.status !== 200) throw new Error('Ein Fehler ist aufgetreten.');
                this.requestStatus = 'rejected';
                this.disableRejection = true;
                this.$emit('requests-updated');
            } catch (error) {
                this.alertError(error.response?.data?.error || error.message);
                if (error.response?.status === 409) this.$emit('requests-updated');
            } finally {
                this.$emit('is-loading', false);
            }
        },

        async accept() {
            if (!this.selectionEnabled || !this.pharmacist) return;
            this.$emit('is-loading', true);
            this.disableAcceptance = true;
            this.disableRejection = true;
            this.$emit('selected-user', this.pharmacist);
            this.isDialogOpen = false;
        },

        openRejectionDialog() {
            if (!this.selectionEnabled || this.disableRejection) return;
            this.isRejectionDialogOpen = true;
        },

        async hydrateApplicant() {
            if (this.isHydrating) return;
            this.isHydrating = true;
            this.hydrationError = false;
            this.$emit('hydration-started');
            try {
                await this.getPharmacist();
                await this.getAppointmentCosts();
            } catch (error) {
                this.pharmacist = null;
                this.hydrationError = true;
                this.alertError(error?.message || 'Freiberufler konnte nicht geladen werden.');
            } finally {
                this.isHydrating = false;
                this.$emit('hydration-finished');
            }
        },

        ...mapMutations({
            alertError: 'alert/error',
        })
    },
    computed: {
        requestDate() {
            const rawDate = this.request?.[3];
            const requestDate = typeof rawDate === "object"
                ? new Date(rawDate["$date"])
                : rawDate
            return this.$formatDate(requestDate)
        },
        travelCostColor() {
            if (!this.travelcostsInfo || this.travelcostsInfo.costs == null) return '';
            return this.travelcostsInfo.is_capped ? 'red' : 'green';
        },
        travelCostDistance() {
            return this.formatDistance(this.travelcostsInfo?.distance);
        },
        travelCostCosts() {
            const costs = this.travelcostsInfo?.costs;
            return costs === undefined || costs === null || costs === '' ? '-' : costs;
        },

        getPharmacistAvailabilityStatus() {
            const isAllocated = this.isDateMatch(this.pharmacist.allocated_dates);
            if (isAllocated) return {
                classes: { ...this.getStatusCss('warning') },
                canAssign: false
            }

            const isUnavailable = this.isDateMatch(this.pharmacist.unavailable_dates);
            if (isUnavailable) return {
                classes: { ...this.getStatusCss('error') },
                canAssign: false
            }

            const availableDates = Array.isArray(this.pharmacist.available_dates)
                ? this.pharmacist.available_dates
                : [];
            const isAvailable = availableDates.some(date => {
                return moment(date).isSame(moment(this.appointment.date))
            });
            if (isAvailable) return {
                classes: { ...this.getStatusCss('success') },
                canAssign: true
            }

            return { canAssign: true }
        },

        getStatusName() {
            return STATUS_MAP[this.requestStatus.toLowerCase()] || ''
        },
        isKnownPharmacist() {
            return (this.knownPharmacists || []).some(id => id === this.pharmacistId)
        },
        isFavoritePharmacist() {
            return this.pharmacistId in (this.pharmacy?.contractor_favorite_list || {});
        },
    },
    watch: {
        hydrationRetryToken() {
            if (this.hydrationError) this.hydrateApplicant();
        },
    },
    created() {
        this.hydrateApplicant();
    },
}
</script>

<style scoped>
td, th {
    text-align: center;
}
.pharmacist-name {
    color: #fff;
    border-radius: 7px;
    padding: 2px 10px;
}
.pharmacist-name a {
    color: #fff;
}
.already-allocated, .already-allocated:hover {
    background: #e0ffe0 !important;
}
.favorite {
    color: #DFA000 !important;
}
</style>
